import React, { useMemo, useState, useEffect } from 'react';
import { Form } from 'semantic-ui-react';
import PropTypes from "prop-types";
import { onError, onSuccess } from '../../../components/shared/messages';
import { useTextResources } from '../../../language';
import { groupByIntoArray } from '../../../components/shared/utils';
import { PromotionExcludeEntityTypes, PromotionExcludeEntityTypeEnum, EntityType } from '../../../components/shared/constants';
import { RateAssignment, RateDiscountExcludedEntities } from '../../../api/services/next';
import DropDown from '../../../components/shared/dropdown';
import ExcludeCustomersTransfer from './transfers/ExcludeCustomersTransfer';
import ExcludeCustomerGroupsTransfer from './transfers/ExcludeCustomerGroupsTransfer';
import EditEntityPopup from '../../../components/shared/EditEntityPopup';
import { ERateCategory} from '../../../components/shared/constants'
import { SyncRateToChannelManager } from '../../../api/services/pms'
import { useLoader } from '../../../hooks/useLoader';

export const ExcludedCustomerPopup = ({
    open,
    onClose,
    currentPromotion
}) => {
    const text = useTextResources();
    const [selectedExcludeEntityType, setSelectedExcludeEntityType] = useState(PromotionExcludeEntityTypeEnum.Customer);
    const [excludedEntities, setExcludedEntities] = useState([]);
    const [rateAssignments, setRateAssignments] = useState([]);
    const [newExcludedEntityIds, setNewExcludedEntityIds] = useState([]);
    const [isReloadNeeded, setIsReloadNeeded] = useState(true);

    const loader = useLoader();

    const excludeEntityTypesDropDownData = useMemo(() => [
        { Id: PromotionExcludeEntityTypeEnum.Customer, Name: text(PromotionExcludeEntityTypes[PromotionExcludeEntityTypeEnum.Customer]) },
        { Id: PromotionExcludeEntityTypeEnum.CustomerGroup, Name: text(PromotionExcludeEntityTypes[PromotionExcludeEntityTypeEnum.CustomerGroup]) },
    ], [text]);

    useEffect(() => {
        (async () => {
            if (!isReloadNeeded) {
                return;
            }

            try {
                loader(true);

                if (currentPromotion) {
                    const rateIds = currentPromotion.RateDiscountAssignments.map(r => r.RateId);
                    const [promotionRateAssignments, promotionExcludedEntities] = await Promise.all([RateAssignment.GetRateAssignments({
                        EntityTypeId: EntityType.Customer,
                        RateIds: rateIds,
                        SkipCache: false,
                        Includes: [0]
                    }), RateDiscountExcludedEntities.GetRateDiscountExcludedEntities({
                        DiscountId: currentPromotion.Id
                    })]);

                    setRateAssignments(promotionRateAssignments);
                    setExcludedEntities(promotionExcludedEntities);
                }

                setIsReloadNeeded(false);
            } catch (error) {
                onError(error);
            }
            finally {
                loader(false);
            }

        })();

    }, [currentPromotion, selectedExcludeEntityType, isReloadNeeded]);

    const promotionCustomerGroups = useMemo(() => {
        const groups = rateAssignments.map(ra =>
            ra.CustomerGroupAssignments.map(rga => {
                return { ...rga, Rate: ra.Rate?.Name }
            })).flat();
        const result = groupByIntoArray(groups, 'CustomerGroupId')
            .map((groupDuplicates) => {
                const group = groupDuplicates[0];
                return {
                    Id: group.CustomerGroupId,
                    Name: group.CustomerGroup.Name,
                    Rates: [... new Set(groupDuplicates.map(g => g.Rate))]
                };
            });
        return result;
    }, [rateAssignments]);

    const excludedCustomerGroups = useMemo(() => {
        return excludedEntities.filter(e => e.EntityTypeId === EntityType.CustomerGroup);
    }, [excludedEntities]);

    const excludedCustomerGroupsIds = useMemo(() => {
        return excludedCustomerGroups.map(ec => ec.EntityId);
    }, [excludedCustomerGroups]);

    const promotionCustomers = useMemo(() => {
        const customers = groupByIntoArray(rateAssignments, 'EntityId')
            .map((customerDuplicates) => {
                const customer = customerDuplicates[0];
                return {
                    EntityId: customer.EntityId,
                    Name: customer.CustomerName,
                    Rates: customerDuplicates.map(c => c.Rate),
                    CustomerGroupAssignments: customerDuplicates.map(c => c.CustomerGroupAssignments).flat()
                };
            });

        const result = customers
            .filter(c => c.CustomerGroupAssignments.length === 0 ||
                !c.CustomerGroupAssignments.every(g => excludedCustomerGroupsIds.includes(g.CustomerGroupId)));
        return result;
    }, [rateAssignments, excludedCustomerGroupsIds]);

    const excludedCustomers = useMemo(() => {
        const result = excludedEntities
            .filter(e => e.EntityTypeId === EntityType.Customer)
            .filter(e => promotionCustomers.find(c => c.EntityId === e.EntityId));
        return result;
    }, [excludedEntities, promotionCustomers]);

    const excludedCustomersIds = useMemo(() => {
        const result = excludedCustomers.map(ec => ec.EntityId);
        return result;
    }, [excludedCustomers,]);

    const saveAction = async (close) => {
        try {
            loader(true);
            const newIds = newExcludedEntityIds.map(id => Number(id));
            if (selectedExcludeEntityType === PromotionExcludeEntityTypeEnum.Customer) {
                await Promise.all([
                    RateDiscountExcludedEntities.DeleteRateDiscountExcludedEntities(excludedCustomers
                        .filter(ec => !newIds.includes(ec.EntityId))
                        .map(ec => ec.Id)
                    ),
                    RateDiscountExcludedEntities.SaveRateDiscountExcludedEntities(newIds
                        .filter(id => !excludedCustomers.find(ec => ec.EntityId === id))
                        .map(id => {
                            return {
                                DiscountId: currentPromotion.Id,
                                EntityTypeId: EntityType.Customer,
                                EntityId: id
                            }
                        })
                    )
                ]);
            }
            else {
                await Promise.all([
                    RateDiscountExcludedEntities.DeleteRateDiscountExcludedEntities(excludedCustomerGroups
                        .filter(ec => !newIds.includes(ec.EntityId))
                        .map(ec => ec.Id)
                    ),
                    RateDiscountExcludedEntities.SaveRateDiscountExcludedEntities(newIds
                        .filter(id => !excludedCustomerGroups.find(ec => ec.EntityId === id))
                        .map(id => {
                            return {
                                DiscountId: currentPromotion.Id,
                                EntityTypeId: EntityType.CustomerGroup,
                                EntityId: id
                            }
                        })
                    )
                ]);
            }
              try {
                  await SyncRateToChannelManager({
                      PMSHotelID:  currentPromotion?.Rates?.find((rate) => rate.HotelId)?.HotelId,
                      ExternalPMSRateID: currentPromotion.Id,
                      eRateCategory: ERateCategory.MultiRateDiscount
                  });
              } catch (error) {
                  onError(error);
              }

            setIsReloadNeeded(true);
            setNewExcludedEntityIds([]);
            onSuccess();
        } catch (error) {
            onError(error);
        }
        finally {
            loader(false);

            if (close) {
                closeAction();
            }
        }
    }

    const closeAction = () => {
        if (onClose) onClose();
    }

    return (
        <EditEntityPopup
            open={open}
            header={`${text("Assignments")}`}
            onClose={closeAction}
            onSave={() => saveAction(false)}
            onSaveAndClose={() => saveAction(true)}
        >
            <Form>
                <Form.Field
                    label={`${text('Customers')} / ${text('CustomerGroups')}`}
                    control={DropDown}
                    data={excludeEntityTypesDropDownData}
                    value={selectedExcludeEntityType}
                    onChange={(newValue) => {
                        setNewExcludedEntityIds([]);
                        setSelectedExcludeEntityType(newValue);
                    }}
                    textField='Name'
                    valueField='Id'
                />
                {
                    selectedExcludeEntityType === PromotionExcludeEntityTypeEnum.Customer ?
                        <Form.Field
                            control={ExcludeCustomersTransfer}
                            promotionCustomers={promotionCustomers}
                            promotionExcludedCustomersIds={excludedCustomersIds}
                            onChange={(newExcludedEntitiesIds) => setNewExcludedEntityIds(newExcludedEntitiesIds)}
                        /> : <Form.Field
                            control={ExcludeCustomerGroupsTransfer}
                            promotionCustomerGroups={promotionCustomerGroups}
                            promotionExcludedCustomerGroups={excludedCustomerGroupsIds}
                            onChange={(newExcludedEntities) => setNewExcludedEntityIds(newExcludedEntities)}
                        />
                }
            </Form>
        </EditEntityPopup>
    );
}

ExcludedCustomerPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSaveComplete: PropTypes.func,
    currentPromotion: PropTypes.any,
};

export default ExcludedCustomerPopup;
